/**
 * @component PopularBooks
 * @description 
 * A component that displays a curated selection of book recommendations, similar to a bookstore's front table.
 * It shows different categories of books (Front Table, Quick Reads, Conversation Starters, etc.) that are
 * periodically refreshed using Google Gemini AI recommendations.
 * 
 * Key Features:
 * - Universal Access: Visible to all visitors without requiring authentication
 * - Consistent Display: Shows the same recommendations to all users
 * - Category Filters: Multiple curated categories with specific AI prompts
 * - Auto-Refresh: Recommendations are automatically refreshed every 7 days
 * - Book Modal: Click on any book to see detailed information
 * 
 * Restrictions:
 * - Adding to Shelf: Requires user authentication
 * - Cache Duration: Book recommendations are cached for 7 days before refresh
 * - Content Source: All recommendations are generated via Google Gemini AI
 * 
 * Usage:
 * ```jsx
 * <PopularBooks />
 * ```
 */

import { useState, useRef, useEffect } from 'react';
import BookImage from './BookImage';
import BookModal from './BookModal';
import { useAuth } from './Auth/AuthContext';
import LoginModal from './Auth/LoginModal';
import LoadingModal from './LoadingModal';
import { API_BASE_URL } from '../config/api';

const filters = [
  { id: 'all', label: 'Front Table', icon: '📚',
    aiPrompt: 'Find at least 8 books that meet ALL of these criteria:\n- Have strong reader engagement and positive reception\n- Have been published in the past 12 months\n- Include unique perspectives or unconventional storytelling\n- Cross multiple genres or defy easy categorization\n- Received praise from independent bookstores or literary blogs\n\nEnsure a mix of both well-known and lesser-known titles.' },
  { id: 'quick_reads', label: 'Quick Reads', icon: '⚡',
    aiPrompt: 'Find at least 8 engaging books published in the last 5 years that can be read in one sitting (under 300 pages). Focus on pacing and impact. I need both popular and lesser-known titles.' },
  { id: 'conversation_starters', label: 'Conversation Starters', icon: '💭',
    aiPrompt: 'Identify at least 8 books published in the last 2 years that are currently trending and sparking social discussions and cultural conversations. I need both popular and lesser-known titles.' },
  { id: 'genre_benders', label: 'Genre Benders', icon: '🌀',
    aiPrompt: 'Find at least 8 innovative books published in the last 4 years that defy traditional genre conventions and blend multiple styles in creative ways. Focus on books that push boundaries and experiment with form. I need both popular and lesser-known titles.' },
  { id: 'fresh_perspectives', label: 'Fresh Perspectives', icon: '🌱',
    aiPrompt: 'Find at least 8 books published in the last 2 years that offer innovative storytelling or unique viewpoints from diverse voices. I need both popular and lesser-known titles.' },
  { id: 'thought_provoking', label: 'Thought-Provoking', icon: '🧠',
    aiPrompt: 'Identify at least 8 contemporary books published in the last 5 years that challenge readers intellectually and spark deep reflection. I need both popular and lesser-known titles.' }
];

const shimmerAnimation = `
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

@keyframes float {
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
}

@keyframes pulse-purple {
  0% { box-shadow: 0 0 0 0 rgba(147, 51, 234, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(147, 51, 234, 0); }
  100% { box-shadow: 0 0 0 0 rgba(147, 51, 234, 0); }
}

@keyframes sparkleOut {
  0% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(0);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) rotate(var(--angle)) translateY(-40px);
    opacity: 0;
  }
}

@keyframes sparklePulse {
  0% {
    transform: scale(0.5);
    opacity: 1;
  }
  50% {
    transform: scale(1.5);
    opacity: 0.8;
  }
  100% {
    transform: scale(0);
    opacity: 0;
  }
}

@keyframes addToShelf {
  0% { transform: scale(1); }
  50% { transform: scale(1.3); }
  100% { transform: scale(1); background-color: rgb(243 244 246); }
}

@keyframes successPop {
  0% { transform: scale(1); }
  50% { transform: scale(1.5) rotate(10deg); }
  100% { transform: scale(1); }
}`;

const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = shimmerAnimation;
document.head.appendChild(styleSheet);

// Book Card Component
const BookCard = ({ book, onBookClick, shelvedBooks, shelvedBooksStatus, setShelvedBooks, setShelvedBooksStatus }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [addToShelfStatus, setAddToShelfStatus] = useState(null);
  const { isAuthenticated, refreshAuth } = useAuth();
  const pendingBookRef = useRef(null);

  // Use shared shelf data instead of individual API calls
  const bookId = book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
  const isInShelf = shelvedBooks.has(bookId);
  const bookShelfStatus = shelvedBooksStatus[bookId] || addToShelfStatus;

  const handleAddToShelf = async (skipAuthCheck = false) => {
    console.log('handleAddToShelf called', { skipAuthCheck, isAuthenticated });
    
    if (!skipAuthCheck && !isAuthenticated) {
      console.log('User not authenticated, showing login modal');
      pendingBookRef.current = book;
      setShowLoginModal(true);
      return;
    }

    try {
      const bookId = book.id || `book_${book.title.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      console.log('Adding book to shelf:', { bookId, title: book.title });
      
      const response = await fetch(`${API_BASE_URL}/api/shelf/books`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          bookId,
          title: book.title,
          author: book.author,
          thumbnail: book.thumbnail,
          description: book.explanation,
          rating: book.rating,
          year: book.year,
          category: book.category
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Session expired, refresh auth state and show login modal
          await refreshAuth();
          pendingBookRef.current = book;
          setShowLoginModal(true);
          return;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to add book to shelf');
      }

      console.log('Successfully added book to shelf');
      // Update shared shelf data
      setShelvedBooks(prev => new Set([...prev, bookId]));
      setShelvedBooksStatus(prev => ({ ...prev, [bookId]: 'success' }));
      setAddToShelfStatus('success');
      setTimeout(() => {
        setAddToShelfStatus(null);
        setShelvedBooksStatus(prev => ({ ...prev, [bookId]: null }));
      }, 2000);
    } catch (error) {
      console.error('Error adding book to shelf:', error);
      setShelvedBooksStatus(prev => ({ ...prev, [bookId]: 'error' }));
      setAddToShelfStatus('error');
      setTimeout(() => {
        setAddToShelfStatus(null);
        setShelvedBooksStatus(prev => ({ ...prev, [bookId]: null }));
      }, 2000);
    }
  };

  // Check for pending book after login
  useEffect(() => {
    if (isAuthenticated && pendingBookRef.current) {
      console.log('Found pending book after login:', pendingBookRef.current.title);
      handleAddToShelf(true);
      pendingBookRef.current = null;
    }
  }, [isAuthenticated]);

  const handleBookClick = (e) => {
    // Prevent the click from triggering the add to shelf button
    e.stopPropagation();
    onBookClick(book);
  };

  return (
    <div className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[200px]">
      <div className="relative group">
        <div 
          className="relative aspect-[2/3] rounded-lg sm:rounded-xl overflow-hidden shadow-md transition-transform group-hover:scale-105 cursor-pointer"
          onClick={handleBookClick}
        >
          <BookImage
            src={book.thumbnail}
            alt={book.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4">
              <div className="flex items-center gap-1 text-white">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                <span className="text-xs sm:text-sm">{book.rating}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2 sm:mt-3">
          <div className="flex items-start justify-between gap-2">
            <h3 
              className="text-sm sm:text-base font-medium text-gray-900 line-clamp-2 font-space flex-1 cursor-pointer hover:text-purple-600 transition-colors"
              onClick={handleBookClick}
            >
              {book.title}
            </h3>
            <button 
              onClick={() => handleAddToShelf()}
              disabled={isInShelf}
              className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full transition-all duration-300 group relative shadow-sm hover:shadow-md ${
                isInShelf
                  ? 'bg-gray-100 text-gray-400'
                  : bookShelfStatus === 'success'
                  ? 'bg-gray-100 text-gray-400 animate-addToShelf'
                  : bookShelfStatus === 'error'
                  ? 'bg-red-500 text-white'
                  : 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:from-purple-600 hover:to-blue-600'
              }`}
              aria-label={isInShelf ? 'Already in shelf' : 'Add to shelf'}
            >
              {/* Sparkle animation container */}
              {bookShelfStatus === 'success' && (
                <div className="absolute inset-0 overflow-visible pointer-events-none">
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute left-1/2 top-1/2 w-1.5 h-1.5 rounded-full"
                      style={{
                        background: i % 2 ? 'rgb(147, 51, 234)' : 'rgb(59, 130, 246)',
                        animation: 'sparkleOut 0.6s ease-out forwards',
                        animationDelay: `${i * 0.05}s`,
                        transform: `translate(-50%, -50%) rotate(${i * 45}deg)`,
                        '--angle': `${i * 45}deg`,
                      }}
                    >
                      <div
                        className="absolute inset-0 rounded-full"
                        style={{
                          animation: 'sparklePulse 0.6s ease-out forwards',
                          animationDelay: `${i * 0.05}s`,
                          background: 'inherit',
                          boxShadow: '0 0 4px currentColor',
                        }}
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 rounded-full transition-colors duration-300" />
              <svg 
                className={`w-[18px] h-[18px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-transform duration-300 group-hover:scale-110 ${
                  bookShelfStatus === 'success' ? 'scale-110 animate-successPop' : ''
                }`}
                fill="currentColor"
                viewBox="0 0 24 24"
                stroke="none"
              >
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16z"/>
              </svg>
            </button>
          </div>
          <p 
            className="mt-1 text-xs sm:text-sm text-gray-600 line-clamp-1 cursor-pointer hover:text-purple-600 transition-colors"
            onClick={handleBookClick}
          >
            {book.author}
          </p>
        </div>
      </div>
      <LoginModal 
        isOpen={showLoginModal} 
        onClose={() => setShowLoginModal(false)} 
      />
    </div>
  );
};

// Custom loading messages for popular books - SIMPLIFIED
const popularBooksLoadingMessages = [
  "Curating the hottest books right now...",
  "Finding the most talked-about books...",
  "Gathering recommendations from book lovers..."
];

// Additional loading messages for refresh state - SIMPLIFIED
const refreshingMessages = [
  "Our AI is generating fresh recommendations...",
  "Refreshing our book recommendations...",
  "Almost there! Finalizing recommendations..."
];

function PopularBooks() {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const scrollContainerRef = useRef(null);
  const [loadingMessage, setLoadingMessage] = useState(popularBooksLoadingMessages[0]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const activeRequestRef = useRef(null);
  
  // BookModal state
  const [selectedBook, setSelectedBook] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Shared shelf data to avoid individual API calls
  const [shelvedBooks, setShelvedBooks] = useState(new Set());
  const [shelvedBooksStatus, setShelvedBooksStatus] = useState({});
  const { isAuthenticated, refreshAuth } = useAuth();

  // Track category click
  const trackCategoryClick = async (categoryId) => {
    try {
      await fetch(`${API_BASE_URL}/api/analytics/category-click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ category: categoryId })
      });
    } catch (error) {
      console.error('Error tracking category click:', error);
      // Don't show error to user, just log it
    }
  };

  // Handle filter selection with tracking
  const handleFilterSelect = (filterId) => {
    setSelectedFilter(filterId);
    trackCategoryClick(filterId);
  };

  // Rotate through loading messages - SIMPLIFIED INTERVAL
  useEffect(() => {
    let interval;
    
    if (loading) {
      interval = setInterval(() => {
        setLoadingMessage(prevMessage => {
          const messages = isRefreshing ? refreshingMessages : popularBooksLoadingMessages;
          const currentIndex = messages.indexOf(prevMessage) % messages.length;
          const nextIndex = (currentIndex + 1) % messages.length;
          return messages[nextIndex];
        });
      }, 2500); // Slower rotation
    }

    return () => clearInterval(interval);
  }, [loading, isRefreshing]);

  const fetchBooksForFilter = async (filterId) => {
    // Cancel any ongoing request
    if (activeRequestRef.current) {
      activeRequestRef.current.aborted = true;
    }
    
    // Create a new request tracker
    const thisRequest = { aborted: false };
    activeRequestRef.current = thisRequest;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log(`Fetching data for ${filterId} from ${API_BASE_URL}/api/recommendations/popular/${filterId}`);
      // Popular books endpoint is public, no credentials needed
      const response = await fetch(`${API_BASE_URL}/api/recommendations/popular/${filterId}`);
      
      // If this request was aborted while waiting for response, ignore the result
      if (thisRequest.aborted) {
        console.log('Request was aborted, ignoring response');
        return;
      }
      
      // Handle 202 Accepted response (recommendations being refreshed) - SIMPLIFIED
      if (response.status === 202) {
        const data = await response.json();
        console.log('API is refreshing recommendations:', data);
        
        // Update UI to show refreshing state
        setIsRefreshing(true);
        setLoadingMessage(refreshingMessages[0]);
        
        // Simple retry after 30 seconds (no complex retry logic)
        setTimeout(() => {
          if (!thisRequest.aborted) {
            console.log(`Retrying fetch for ${filterId} after 30s delay`);
            fetchBooksForFilter(filterId);
          }
        }, 30000);
        
        return;
      }
      
      // Handle other non-200 responses
      if (!response.ok) {
        throw new Error(`Failed to fetch recommendations: ${response.status} ${response.statusText}`);
      }
      
      // Handle successful response
      const data = await response.json();
      handleSuccessResponse(data);
      
    } catch (err) {
      if (thisRequest.aborted) return;
      
      console.error('Error fetching recommendations:', err);
      setError('Failed to load recommendations. Please try again later.');
      setBooks([]);
      setIsRefreshing(false);
    } finally {
      if (!thisRequest.aborted) {
        setLoading(false);
        setIsInitialLoad(false);
      }
    }
  };
  
  // Helper function to handle successful API responses
  const handleSuccessResponse = (data) => {
    // Handle the new response format that includes books and metadata
    const booksArray = data.books || data;
    
    // Check if we have a valid books array
    if (!Array.isArray(booksArray) || booksArray.length === 0) {
      setError('No recommendations available at this time. Please try again later.');
      setBooks([]);
      return;
    }
    
    // If we got here, we have a valid books array
    setBooks(booksArray);
    setIsRefreshing(false);
  };

  // Fetch shelved books when component mounts or auth state changes
  useEffect(() => {
    const fetchShelvedBooks = async () => {
      if (!isAuthenticated) {
        setShelvedBooks(new Set());
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/shelf/books`, {
          credentials: 'include',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          }
        });
        
        if (!response.ok) {
          if (response.status === 401) {
            await refreshAuth();
            return;
          }
          throw new Error('Failed to fetch shelf');
        }
        
        const books = await response.json();
        const bookIds = new Set(books.map(b => b.bookId));
        setShelvedBooks(bookIds);
        console.log('PopularBooks: Fetched shelved books:', bookIds.size, 'books');
      } catch (error) {
        console.error('Error fetching shelved books:', error);
        setShelvedBooks(new Set());
      }
    };

    fetchShelvedBooks();
  }, [isAuthenticated]);

  // Modified to focus on loading current filter
  useEffect(() => {
    // Only load the current filter
    fetchBooksForFilter(selectedFilter);
    
    // Cleanup function to abort any pending requests when component unmounts or filter changes
    return () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.aborted = true;
        activeRequestRef.current = null;
      }
    };
  }, [selectedFilter]);

  const handleScroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = direction === 'left' ? -200 : 200;
      scrollContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleWheel = (e) => {
      e.preventDefault();
      container.scrollLeft += e.deltaY;
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleBookClick = (book) => {
    setSelectedBook(book);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedBook(null);
  };

  return (
    <>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-6">
          {filters.map((filter) => (
            <button
              key={filter.id}
              onClick={() => handleFilterSelect(filter.id)}
              className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                selectedFilter === filter.id
                  ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="mr-1.5">{filter.icon}</span>
              {filter.label}
            </button>
          ))}
        </div>

        <div className="relative">
          {/* Show loading state */}
          {loading && (
            <div className="w-full py-16 flex flex-col items-center justify-center bg-white/50 backdrop-blur-sm rounded-xl shadow-sm">
              <div className="flex items-center justify-center mb-4">
                <svg viewBox="0 0 200 200" className="w-24 h-24">
                  <defs>
                    <radialGradient id="dotGradient" cx="50%" cy="50%" r="50%" fx="50%" fy="50%">
                      <stop offset="0%" stopColor="rgb(147, 51, 234)" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="0.3" />
                    </radialGradient>
                  </defs>
                  
                  {/* Animated dots */}
                  <circle cx="80" cy="80" r="6" fill="url(#dotGradient)" className="animate-pulse">
                    <animate attributeName="cy" values="80;60;80" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="120" cy="80" r="6" fill="url(#dotGradient)" className="animate-pulse">
                    <animate attributeName="cy" values="80;100;80" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                  <circle cx="100" cy="100" r="6" fill="url(#dotGradient)" className="animate-pulse">
                    <animate attributeName="cy" values="100;80;100" dur="1.5s" repeatCount="indefinite" />
                  </circle>
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-900 text-center">{loadingMessage}</p>
              {isRefreshing && retryCount > 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  {retryCount > 1 ? `Retry attempt ${retryCount}...` : "Retrying..."}
                </p>
              )}
            </div>
          )}

          {!loading && (
            <>
              <button
                onClick={() => handleScroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110"
                aria-label="Scroll left"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <div
                ref={scrollContainerRef}
                className="overflow-x-auto hide-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <div className="flex gap-4 sm:gap-6 py-4">
                  {error ? (
                    <div className="flex flex-col items-center justify-center w-full py-12 text-center">
                      <p className="text-gray-700 font-medium mb-2">{error}</p>
                      <button 
                        onClick={() => {
                          setLoading(true);
                          setRetryCount(0);
                          fetchBooksForFilter(selectedFilter);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105"
                      >
                        Try Again
                      </button>
                    </div>
                  ) : (
                    Array.isArray(books) ? books.map((book) => (
                      <BookCard 
                        key={book.id || `book_${book.title}`} 
                        book={book} 
                        onBookClick={handleBookClick}
                        shelvedBooks={shelvedBooks}
                        shelvedBooksStatus={shelvedBooksStatus}
                        setShelvedBooks={setShelvedBooks}
                        setShelvedBooksStatus={setShelvedBooksStatus}
                      />
                    )) : (
                      <div className="flex items-center justify-center w-full py-12 text-gray-500">
                        <p>No books available. Please try again later.</p>
                      </div>
                    )
                  )}
                </div>
              </div>

              <button
                onClick={() => handleScroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-110"
                aria-label="Scroll right"
              >
                <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      {/* BookModal */}
      <BookModal
        book={selectedBook}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
      />
    </>
  );
}

export default PopularBooks; 